// Hook: liga sync de grafo + awareness para um workspace.
// Retorna handle com mutators (upsertNode etc.) e função para publicar cursor.

import { useEffect, useMemo, useRef } from 'react';
import { attachGraphSync, type GraphSyncHandle } from './graph-sync.js';
import { publishCursor, readRemoteCursors } from './awareness.js';
import { useStore } from '../store/useStore.js';

export interface GraphRealtime {
  handle: GraphSyncHandle | null;
  publishCursor: (worldX: number, worldY: number) => void;
}

export function useGraphRealtime(workspaceId: string | undefined): GraphRealtime {
  const handleRef = useRef<GraphSyncHandle | null>(null);

  useEffect(() => {
    if (!workspaceId) return;
    const h = attachGraphSync(workspaceId);
    handleRef.current = h;

    const setSync = useStore.getState().setSyncStatus;
    const setCursors = useStore.getState().setRemoteCursors;

    h.provider.provider.on('status', (e: { status: string }) => {
      if (e.status === 'connected') setSync('online');
      else if (e.status === 'disconnected') setSync('offline');
    });

    // After Hocuspocus finishes the initial sync, allow observeDeep to react to
    // real remote-peer changes. Before this fires, the initial sync updates
    // (transactionOrigin = provider) are ignored to prevent overwriting the
    // REST-hydrated store with stale/empty Yjs server state.
    h.provider.provider.on('synced', () => {
      h.markSynced();
    });

    h.provider.provider.on('awarenessChange', () => {
      const selfId = h.provider.provider.awareness?.clientID ?? -1;
      const aw = h.provider.provider.awareness;
      if (!aw) return;
      setCursors(readRemoteCursors(aw, selfId));
    });

    return () => {
      h.release();
      handleRef.current = null;
      useStore.getState().setRemoteCursors([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // Throttle de awareness — publica no máximo a cada 50ms.
  const lastPubRef = useRef(0);
  const api = useMemo<GraphRealtime>(
    () => ({
      get handle() {
        return handleRef.current;
      },
      publishCursor(worldX, worldY) {
        const now = Date.now();
        if (now - lastPubRef.current < 50) return;
        lastPubRef.current = now;
        const aw = handleRef.current?.provider.provider.awareness;
        if (!aw) return;
        const st = useStore.getState();
        publishCursor(aw, { userId: st.userId, name: st.name, worldX, worldY, selectedNodeId: st.selectedId });
      },
    }),
    [],
  );

  return api;
}
